// The workspace, as in cmux: each sidebar row owns a workspace (a group), a split of 1–4 panes, each split right or
// down from another (a split tree) and sized by their dividers; each pane holds tabs, one session each, and shows one
// of them. Picking a row shows its group as it was left, and never opens, adds or replaces a tab; + adds a blank tab
// to its pane, offering what to show, and the session picked there takes its place and belongs to the row (it has no
// row of its own). A session outside every
// group is a row of its own (one pane, one tab) the first time it's picked. The desktop, Files, Settings and the
// machine are pages: they take the place of the panes, never split, and the group behind stays as it was. Closing a
// tab never ends its session (it is a row again); a session that ends leaves its pane. The groups live on the machine
// (workspaceSync.ts), so every device shows the same; the row shown, the page and blank tabs are this browser's.

import type { LayoutNode, Session } from '../net/protocol';

/** What a page shows in place of the panes. */
export type Page = 'desktop' | 'files' | 'settings' | 'machine';

export const MAX_PANES = 4;

/** Where the panes sit: a pane, or a split of two or more side by side (row) or stacked (col), sized as fractions
 *  that add up to 1. Leaves hold indexes into `panes`. */
export type Layout = LayoutNode;

/** A pane: its tabs (sessions, in strip order) and the one it shows. A pane split off a moment ago has none yet: it
 *  offers what to show, as a blank tab does. */
export interface Pane {
  tabs: number[];
  active: number;
}

/** A row's workspace. `lead` is the session whose row it is (the first one left when that one goes). */
export interface Group {
  id: number;
  lead: number;
  panes: Pane[];
  focused: number;
  layout: Layout;
  /** The machine's (tmux's resize-pane -Z): the focused pane fills the workspace, the others wait behind it. Never
   *  set here, only cleared (unzoom, or a pane added or taken away, as tmux does). */
  zoomed?: boolean;
}

/** Every row's group, the one shown (`at`, a group's id; null for none), and the page over it. */
export interface TabsState {
  groups: Group[];
  at: number | null;
  page: Page | null;
}

const isLeaf = (l: Layout): l is { pane: number } => 'pane' in l;

/** The panes in reading order: left to right, then top to bottom within a column. */
export function paneOrder(l: Layout): number[] {
  return isLeaf(l) ? [l.pane] : l.kids.flatMap(paneOrder);
}

/** A layout for n panes with no tree saved: side by side, and 4 as a 2×2 grid. */
function defaultLayout(n: number): Layout {
  if (n <= 1) return { pane: 0 };
  if (n === 4) return { dir: 'row', sizes: [0.5, 0.5], kids: [{ dir: 'col', sizes: [0.5, 0.5], kids: [{ pane: 0 }, { pane: 2 }] }, { dir: 'col', sizes: [0.5, 0.5], kids: [{ pane: 1 }, { pane: 3 }] }] };
  return { dir: 'row', sizes: equal(n), kids: Array.from({ length: n }, (_, i) => ({ pane: i })) };
}

function validLayout(l: unknown, n: number): l is Layout {
  const seen = new Set<number>();
  const ok = (x: unknown): boolean => {
    if (!x || typeof x !== 'object') return false;
    const o = x as Record<string, unknown>;
    if (typeof o.pane === 'number') {
      if (o.pane < 0 || o.pane >= n || seen.has(o.pane)) return false;
      seen.add(o.pane);
      return true;
    }
    return (o.dir === 'row' || o.dir === 'col') && Array.isArray(o.kids) && Array.isArray(o.sizes) && o.kids.length >= 2 && o.sizes.length === o.kids.length && o.sizes.every((v) => typeof v === 'number' && v > 0) && o.kids.every(ok);
  };
  return ok(l) && seen.size === n;
}

/** Split pane `i`'s leaf: `ni` goes after it (before it with `before`: left or above), in a split of `dir` (joining
 *  its parent's when that runs the same way). */
export function insertLeaf(l: Layout, i: number, ni: number, dir: 'row' | 'col', before = false): Layout {
  if (isLeaf(l)) return l.pane === i ? { dir, sizes: [0.5, 0.5], kids: before ? [{ pane: ni }, l] : [l, { pane: ni }] } : l;
  const k = l.kids.findIndex((c) => isLeaf(c) && c.pane === i);
  if (k >= 0 && l.dir === dir) {
    const half = l.sizes[k] / 2;
    const at = before ? k : k + 1;
    return { ...l, sizes: [...l.sizes.slice(0, k), half, half, ...l.sizes.slice(k + 1)], kids: [...l.kids.slice(0, at), { pane: ni }, ...l.kids.slice(at)] };
  }
  return { ...l, kids: l.kids.map((c) => insertLeaf(c, i, ni, dir, before)) };
}

/** Take pane `i` out: its share goes to the pane before it (or after), a split left with one pane becomes that
 *  pane, and the panes after `i` move down an index. */
function removeLeaf(l: Layout, i: number): Layout {
  const renum = (x: Layout): Layout => (isLeaf(x) ? { pane: x.pane > i ? x.pane - 1 : x.pane } : { ...x, kids: x.kids.map(renum) });
  const drop = (x: Layout): Layout => {
    if (isLeaf(x)) return x;
    const k = x.kids.findIndex((c) => isLeaf(c) && c.pane === i);
    if (k < 0) return { ...x, kids: x.kids.map(drop) };
    const kids = x.kids.filter((_, j) => j !== k);
    const sizes = x.sizes.filter((_, j) => j !== k);
    sizes[Math.max(0, k - 1)] += x.sizes[k];
    if (kids.length === 1) return kids[0];
    return { ...x, kids, sizes };
  };
  // a split nested in one running the same way after a removal folds into it
  const flat = (x: Layout): Layout => {
    if (isLeaf(x)) return x;
    const kids: Layout[] = [];
    const sizes: number[] = [];
    x.kids.map(flat).forEach((c, j) => {
      if (!isLeaf(c) && c.dir === x.dir) {
        c.kids.forEach((cc, m) => {
          kids.push(cc);
          sizes.push(c.sizes[m] * x.sizes[j]);
        });
      } else {
        kids.push(c);
        sizes.push(x.sizes[j]);
      }
    });
    return { ...x, kids, sizes };
  };
  return renum(flat(drop(l)));
}

/** The split node at `path` (indexes into kids from the root). */
function atPath(l: Layout, path: number[], f: (node: Extract<Layout, { dir: string }>) => Layout): Layout {
  if (isLeaf(l)) return l;
  if (!path.length) return f(l);
  return { ...l, kids: l.kids.map((c, j) => (j === path[0] ? atPath(c, path.slice(1), f) : c)) };
}
const evenAll = (l: Layout): Layout => (isLeaf(l) ? l : { ...l, sizes: equal(l.kids.length), kids: l.kids.map(evenAll) });

const equal = (n: number) => Array.from({ length: n }, () => 1 / n);

const KEY = 'cmx.tabs3';
const OLD_KEY = 'cmx.tabs2';
const PAGES: Page[] = ['desktop', 'files', 'settings', 'machine'];

export function emptyTabs(): TabsState {
  return { groups: [], at: null, page: null };
}

/** The group shown (whatever page is over it). */
export const current = (s: TabsState): Group | null => s.groups.find((g) => g.id === s.at) ?? null;

/** A blank tab (+) has a negative id, never a session's: it offers what to show as an empty pane does, has no row,
 *  and is gone at the next load. */
export const isBlank = (id: number) => id < 0;

/** A group's sessions in pane order, each pane's tabs in strip order (blank tabs aren't sessions). */
export const members = (g: Group): number[] => paneOrder(g.layout).flatMap((i) => (g.panes[i]?.tabs ?? []).filter((id) => !isBlank(id)));

/** The group session `id` is in. */
export const groupOf = (s: TabsState, id: number): Group | null => s.groups.find((g) => g.panes.some((p) => p.tabs.includes(id))) ?? null;

/** The session pane `i` of a group shows (none on a blank tab). */
export const shownIn = (g: Group, i: number): number | null => {
  const id = g.panes[i]?.tabs[g.panes[i].active];
  return id === undefined || isBlank(id) ? null : id;
};

/** The session in front: the focused pane's tab, unless a page is over the panes. */
export function focusedSession(s: TabsState): number | null {
  const g = current(s);
  return g && !s.page ? shownIn(g, g.focused) : null;
}

/** Sessions that belong to another session's row: in a group, not its lead. They have no row of their own. */
export function inner(s: TabsState): Set<number> {
  return new Set(s.groups.flatMap((g) => members(g).filter((id) => id !== g.lead)));
}

/** A row's sessions: its group's, or the session alone. */
export const rowSessions = (s: TabsState, lead: number): number[] => {
  const g = s.groups.find((x) => x.lead === lead);
  return g ? members(g) : [lead];
};

/** Ids this app picks for a group it makes: at random in [2^20, 2^31), where the machine never numbers its own. */
export const CLIENT_IDS = 2 ** 20;
const nextId = (s: TabsState) => {
  let id: number;
  do id = CLIENT_IDS + Math.floor(Math.random() * (2 ** 31 - CLIENT_IDS));
  while (s.groups.some((g) => g.id === id));
  return id;
};
const single = (id: number, sid: number): Group => ({ id, lead: sid, panes: [{ tabs: [sid], active: 0 }], focused: 0, layout: { pane: 0 } });

/** A group after a change: its lead moves on when it left; with no session left it's gone (null). */
export function settle(g: Group): Group | null {
  const ids = members(g);
  if (!ids.length) return null;
  return ids.includes(g.lead) ? g : { ...g, lead: ids[0] };
}

/** Put a changed group back (or drop it): the page shown stays, and no group shown when it was the one dropped. */
function put(s: TabsState, id: number, g: Group | null): TabsState {
  const was = s.groups.find((x) => x.id === id);
  const settled = g && settle(g);
  // as tmux: a pane added or taken away shows them all again
  const next = settled && was && settled.panes.length !== was.panes.length ? unzoomed(settled) : settled;
  const groups = next ? s.groups.map((x) => (x.id === id ? next : x)) : s.groups.filter((x) => x.id !== id);
  return { ...s, groups, at: !next && s.at === id ? null : s.at };
}

const unzoomed = (g: Group): Group => (g.zoomed ? { ...g, zoomed: false } : g);

/** Show every pane of the group shown again (its zoomed pane's Zoomed in its bar). */
export function unzoom(s: TabsState): TabsState {
  const g = current(s);
  return g?.zoomed ? put(s, g.id, unzoomed(g)) : s;
}

/** Take pane `i` out of a group: its share goes to a neighbour, the focus to the pane before it. */
export function dropPane(g: Group, i: number): Group | null {
  if (g.panes.length <= 1) return null;
  const order = paneOrder(g.layout);
  const prev = order[Math.max(0, order.indexOf(i) - 1)];
  const keep = g.focused === i ? (prev === i ? order[1] : prev) : g.focused;
  return { ...g, panes: g.panes.filter((_, k) => k !== i), focused: keep > i ? keep - 1 : keep, layout: removeLeaf(g.layout, i) };
}

/** A pane without tab `k`: the one after it shows, else the one before. */
export const withoutTab = (p: Pane, k: number): Pane => {
  const tabs = p.tabs.filter((_, j) => j !== k);
  return { tabs, active: Math.max(0, Math.min(k < p.active ? p.active - 1 : p.active, tabs.length - 1)) };
};

/** Take session `id` out of whatever group holds it: a pane left with no tab closes, a group left with no pane goes. */
function pluck(s: TabsState, id: number): TabsState {
  const g = groupOf(s, id);
  if (!g) return s;
  const i = g.panes.findIndex((p) => p.tabs.includes(id));
  const pane = withoutTab(g.panes[i], g.panes[i].tabs.indexOf(id));
  return put(s, g.id, pane.tabs.length ? { ...g, panes: g.panes.map((p, k) => (k === i ? pane : p)) } : dropPane(g, i));
}

/** Show session `id`'s row (the sidebar, ⌘1–9, the palette): its group as it was left, the page closed. A session
 *  another row holds (a notice for it, say, or its row in the sidebar's group) shows in its pane, and so does the
 *  lead with `reveal`; one no group holds becomes a row's group of its own. */
export function select(s: TabsState, id: number, reveal = false): TabsState {
  const g = groupOf(s, id);
  if (!g) {
    const n = nextId(s);
    return { groups: [...s.groups, single(n, id)], at: n, page: null };
  }
  if (id === g.lead && !reveal && s.at === g.id) return s.page ? { ...s, page: null } : s;
  if (id === g.lead && !reveal) return { ...s, at: g.id, page: null };
  const i = g.panes.findIndex((p) => p.tabs.includes(id));
  const next = { ...g, focused: i, panes: g.panes.map((p, k) => (k === i ? { ...p, active: p.tabs.indexOf(id) } : p)) };
  return { ...s, groups: s.groups.map((x) => (x.id === g.id ? next : x)), at: g.id, page: null };
}

/** Add session `id` as a tab in pane `pane` (the focused one by default) of the group shown, at the end; with no group
 *  shown, it's a row of its own. */
export function addTab(s: TabsState, id: number, pane?: number): TabsState {
  const t = pluck(s, id);
  const g = current(t);
  if (!g) return select(t, id);
  const i = pane !== undefined && pane >= 0 && pane < g.panes.length ? pane : g.focused;
  const p = g.panes[i];
  const next = { ...g, focused: i, panes: g.panes.map((x, k) => (k === i ? { tabs: [...p.tabs, id], active: p.tabs.length } : x)) };
  return { ...put(t, g.id, next), page: null };
}

/** Add a blank tab to pane `pane` (the focused one by default) of the group shown, at the end, where its + was, and
 *  show it: no session until one is picked there. With no group shown, the panes (empty, offering the same). */
export function addBlank(s: TabsState, pane?: number): TabsState {
  const g = current(s);
  if (!g) return { ...s, page: null };
  const i = pane !== undefined && pane >= 0 && pane < g.panes.length ? pane : g.focused;
  const p = g.panes[i];
  const blank = Math.min(0, ...s.groups.flatMap((x) => x.panes.flatMap((q) => q.tabs))) - 1;
  const next = { ...g, focused: i, panes: g.panes.map((x, k) => (k === i ? { tabs: [...p.tabs, blank], active: p.tabs.length } : x)) };
  return { ...put(s, g.id, next), page: null };
}

/** Put session `id` (picked there) in place of the blank tab `blank`, or in the empty pane `i` of the group shown, from
 *  whatever row held it. */
export function fillPane(s: TabsState, i: number, id: number, blank?: number): TabsState {
  const t = pluck(s, id);
  const g = current(t);
  const at = blank === undefined || !g ? -1 : g.panes.findIndex((p) => p.tabs.includes(blank));
  if (g && at >= 0) {
    const k = g.panes[at].tabs.indexOf(blank!);
    return put(t, g.id, { ...g, focused: at, panes: g.panes.map((p, n) => (n === at ? { tabs: p.tabs.map((x) => (x === blank ? id : x)), active: k } : p)) });
  }
  if (!g || !g.panes[i] || g.panes[i].tabs.length) return addTab(t, id);
  return put(t, g.id, { ...g, focused: i, panes: g.panes.map((p, k) => (k === i ? { tabs: [id], active: 0 } : p)) });
}

/** Show tab `k` of pane `i` in the group shown. */
export function activate(s: TabsState, i: number, k: number): TabsState {
  const g = current(s);
  const p = g?.panes[i];
  if (!g || !p || k < 0 || k >= p.tabs.length || (g.focused === i && p.active === k)) return s;
  return put(s, g.id, { ...g, focused: i, panes: g.panes.map((x, j) => (j === i ? { ...x, active: k } : x)) });
}

/** Close tab `k` of pane `i` (its session goes on, a row of its own; a blank tab just goes); the pane's last tab closes
 *  the pane. */
export function closeTab(s: TabsState, i: number, k: number): TabsState {
  const g = current(s);
  const p = g?.panes[i];
  if (!g || !p || k < 0 || k >= p.tabs.length) return s;
  const pane = withoutTab(p, k);
  return put(s, g.id, pane.tabs.length ? { ...g, panes: g.panes.map((x, j) => (j === i ? pane : x)) } : dropPane(g, i));
}

/** Close session `id`'s tab wherever it is (`cmx detach` typed in it, or Remove from split view on its sidebar row):
 *  it's a row of its own again, and the group it left stays shown if it was. */
export function leave(s: TabsState, id: number): TabsState {
  return pluck(s, id);
}

/** Move tab `from` of pane `i` to `to` (dragged along its bar, or Alt+Shift+arrow); the tab shown stays shown. */
export function moveTab(s: TabsState, i: number, from: number, to: number): TabsState {
  const g = current(s);
  const p = g?.panes[i];
  if (!g || !p || from === to || from < 0 || to < 0 || from >= p.tabs.length || to >= p.tabs.length) return s;
  const tabs = [...p.tabs];
  tabs.splice(to, 0, ...tabs.splice(from, 1));
  return put(s, g.id, { ...g, panes: g.panes.map((x, j) => (j === i ? { tabs, active: tabs.indexOf(p.tabs[p.active]) } : x)) });
}

/** Move tab `k` of pane `i` into pane `j` at `at` (a tab dragged onto another pane's bar, or its body: the end), shown
 *  there and that pane focused; a pane left with no tab closes, as closing its last tab does. Never a new pane or
 *  session: within one pane it's a move along its bar. */
export function moveTabTo(s: TabsState, i: number, k: number, j: number, at: number): TabsState {
  const g = current(s);
  const p = g?.panes[i];
  const q = g?.panes[j];
  if (!g || !p || !q || k < 0 || k >= p.tabs.length) return s;
  if (i === j) return moveTab(s, i, k, Math.max(0, Math.min(at, p.tabs.length - 1)));
  const pos = Math.max(0, Math.min(at, q.tabs.length));
  const into: Pane = { tabs: [...q.tabs.slice(0, pos), p.tabs[k], ...q.tabs.slice(pos)], active: pos };
  const left = withoutTab(p, k);
  const next: Group = { ...g, focused: j, panes: g.panes.map((x, n) => (n === j ? into : n === i ? left : x)) };
  return put(s, g.id, left.tabs.length ? next : dropPane(next, i));
}

export type Side = 'left' | 'right' | 'top' | 'bottom';

/** Move tab `k` of pane `i` into a new pane split off pane `j` on `side` (a tab dropped on that edge of a pane, as in
 *  cmux): shown there and focused; a pane left with no tab closes. Its own pane splits only when it has another tab
 *  to keep, and not past 4 panes. Never a new session: the tab moves. */
export function moveTabToSplit(s: TabsState, i: number, k: number, j: number, side: Side): TabsState {
  const g = current(s);
  const p = g?.panes[i];
  if (!g || !p || !g.panes[j] || k < 0 || k >= p.tabs.length || g.panes.length >= MAX_PANES || (i === j && p.tabs.length < 2)) return s;
  const ni = g.panes.length;
  const dir = side === 'left' || side === 'right' ? 'row' : 'col';
  const left = withoutTab(p, k);
  const next: Group = {
    ...g,
    focused: ni,
    panes: [...g.panes.map((x, n) => (n === i ? left : x)), { tabs: [p.tabs[k]], active: 0 }],
    layout: insertLeaf(g.layout, j, ni, dir, side === 'left' || side === 'top'),
  };
  return put(s, g.id, left.tabs.length ? next : dropPane(next, i));
}

/** Each pane's width and height in a layout `w` wide and `h` tall, in pane order. */
function leafSizes(l: Layout, w: number, h: number): { pane: number; w: number; h: number }[] {
  if (isLeaf(l)) return [{ pane: l.pane, w, h }];
  return l.kids.flatMap((c, k) => leafSizes(c, l.dir === 'row' ? w * l.sizes[k] : w, l.dir === 'col' ? h * l.sizes[k] : h));
}

/** Session `id`, dragged onto row `lead` in the sidebar (a row of its own, or one out of another group, which its pane
 *  leaves), joins that row's group as a new pane, shown and focused; its own row goes. The panes fill toward a 2×2:
 *  the largest pane (the last in pane order on a tie) splits along its longer side, wider side by side with the new
 *  pane on the right, taller stacked with it below. `aspect` is the panes' width over their height (a desktop's by
 *  default). Not past 4 panes, nor into the group it's already in. */
export function joinRow(s: TabsState, id: number, lead: number, aspect = 1.6): TabsState {
  const from = groupOf(s, id);
  if (id === lead || isBlank(id) || (from && from === groupOf(s, lead))) return s;
  const t = pluck(s, id);
  const g = groupOf(t, lead);
  const into = g ?? single(nextId(t), lead);
  if (into.panes.length >= MAX_PANES) return s;
  const big = leafSizes(into.layout, aspect, 1).reduce((m, x) => (x.w * x.h >= m.w * m.h - 1e-9 ? x : m));
  const ni = into.panes.length;
  const next: Group = { ...unzoomed(into), panes: [...into.panes, { tabs: [id], active: 0 }], focused: ni, layout: insertLeaf(into.layout, big.pane, ni, big.w >= big.h ? 'row' : 'col') };
  return { groups: g ? t.groups.map((x) => (x.id === g.id ? next : x)) : [...t.groups, next], at: next.id, page: null };
}

/** Split pane `at` (the focused one by default) right or down: the new pane is empty (it offers what to show) and
 *  focused (Arc's: nothing starts by itself). */
export function split(s: TabsState, dir: 'row' | 'col' = 'row', at?: number): TabsState {
  const g = current(s);
  if (!g || g.panes.length >= MAX_PANES) return s;
  const ni = g.panes.length;
  return put(s, g.id, { ...g, panes: [...g.panes, { tabs: [], active: 0 }], focused: ni, layout: insertLeaf(g.layout, at ?? g.focused, ni, dir) });
}

/** Close pane `i` of the group shown: its sessions go on as rows of their own; the last pane closes the group. */
export function closePane(s: TabsState, i: number): TabsState {
  const g = current(s);
  if (!g || !g.panes[i]) return s;
  return put(s, g.id, dropPane(g, i));
}

export function focus(s: TabsState, i: number): TabsState {
  const g = current(s);
  return !g || i === g.focused || i < 0 || i >= g.panes.length ? s : put(s, g.id, { ...g, focused: i });
}

/** Show a page over the panes (null: back to them). */
/** No row shown (New session): the panes empty, offering a new terminal or the last session; what's picked becomes a
 *  row of its own. Nothing is made until then. */
export function showStart(s: TabsState): TabsState {
  return s.at === null && !s.page ? s : { ...s, at: null, page: null };
}

export function openPage(s: TabsState, page: Page | null): TabsState {
  return s.page === page ? s : { ...s, page };
}

/** Swap one session for another everywhere (a restart's new session takes the old one's place, and its row). */
export function replace(s: TabsState, from: number, to: number): TabsState {
  const swap = (id: number) => (id === from ? to : id);
  return { ...s, groups: s.groups.map((g) => ({ ...g, lead: swap(g.lead), panes: g.panes.map((p) => ({ ...p, tabs: p.tabs.map(swap) })) })) };
}

/** Sessions that ended leave their panes: a pane they leave empty closes, and a group with no pane left goes. */
export function reconcile(s: TabsState, sessionIds: number[]): TabsState {
  const alive = new Set(sessionIds);
  const gone = (id: number) => !isBlank(id) && !alive.has(id);
  if (s.groups.every((g) => g.panes.every((p) => !p.tabs.some(gone)))) return s;
  let t = s;
  for (const g of s.groups) for (const p of g.panes) for (const id of p.tabs) if (gone(id)) t = pluck(t, id);
  return t;
}

/** Resize the split at `path` of the group shown. */
export function resize(s: TabsState, path: number[], sizes: number[]): TabsState {
  const g = current(s);
  return g ? put(s, g.id, { ...g, layout: atPath(g.layout, path, (n) => ({ ...n, sizes })) }) : s;
}

/** Make the split at `path` even again (every split when no path is given). */
export function resetSizes(s: TabsState, path?: number[]): TabsState {
  const g = current(s);
  return g ? put(s, g.id, { ...g, layout: path ? atPath(g.layout, path, (n) => ({ ...n, sizes: equal(n.kids.length) })) : evenAll(g.layout) }) : s;
}

function validPane(p: unknown, seen: Set<number>): Pane {
  const o = (p && typeof p === 'object' ? p : {}) as Record<string, unknown>;
  const tabs = (Array.isArray(o.tabs) ? o.tabs : []).filter((id): id is number => typeof id === 'number' && !isBlank(id) && !seen.has(id) && !!seen.add(id));
  return { tabs, active: Math.min(Math.max(0, Number(o.active) || 0), Math.max(0, tabs.length - 1)) };
}

function validGroup(g: unknown, seen: Set<number>): Group | null {
  if (!g || typeof g !== 'object') return null;
  const o = g as Record<string, unknown>;
  if (!Array.isArray(o.panes) || !o.panes.length || typeof o.id !== 'number') return null;
  const panes = (o.panes as unknown[]).slice(0, MAX_PANES).map((p) => validPane(p, seen));
  const layout = validLayout(o.layout, panes.length) ? o.layout : defaultLayout(panes.length);
  const focused = Math.min(Math.max(0, Number(o.focused) || 0), panes.length - 1);
  return settle({ id: o.id, lead: Number(o.lead), panes, focused, layout });
}

/** From before rows had groups (cmx.tabs2): a tab was a group of 1–4 panes, each one view. Each tab with a session
 *  becomes a row's group, its sessions one tab each in the panes they were in (the desktop, Files and the like drop
 *  out: they're pages now); the tab that was in front is the group shown. */
function migrate(old: { groups?: unknown; at?: unknown }): TabsState {
  if (!Array.isArray(old.groups)) return emptyTabs();
  const seen = new Set<number>();
  const groups: Group[] = [];
  let at: number | null = null;
  old.groups.forEach((raw, k) => {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const views = Array.isArray(o.panes) ? (o.panes as unknown[]).slice(0, MAX_PANES) : [];
    let layout: Layout = validLayout(o.layout, views.length) ? (o.layout as Layout) : defaultLayout(views.length);
    let focused = Math.min(Math.max(0, Number(o.focused) || 0), Math.max(0, views.length - 1));
    const panes: Pane[] = [];
    // walk from the end, so taking a pane out doesn't renumber the ones still to look at
    for (let i = views.length - 1; i >= 0; i--) {
      const v = views[i] as { kind?: unknown; id?: unknown } | null;
      if (v?.kind === 'session' && typeof v.id === 'number' && !seen.has(v.id)) {
        seen.add(v.id);
        panes.unshift({ tabs: [v.id], active: 0 });
      } else if (views.length > 1 && panes.length + i > 0) {
        layout = removeLeaf(layout, i);
        focused = focused > i ? focused - 1 : focused === i ? 0 : focused;
      }
    }
    if (!panes.length || !validLayout(layout, panes.length)) return;
    const g = settle({ id: groups.length + 1, lead: -1, panes, focused: Math.min(focused, panes.length - 1), layout });
    if (!g) return;
    groups.push(g);
    if (k === Number(old.at)) at = g.id;
  });
  return { groups, at, page: null };
}

/** What this browser keeps of the workspace: the row shown (by its group's id and its lead, to find it again) and the
 *  page; the groups too until the machine keeps them (`machine`: it does, and they went to it), or always with an
 *  agent from before it did. Once the machine keeps them, the groups are its arrangement as this browser last heard it
 *  (keepArrangement), shown until it says them again. */
export interface Saved {
  tabs: TabsState;
  machine: boolean;
  lead: number | null;
}

/** `machineId`: the machine whose arrangement kept here may show. */
export function loadTabs(machineId?: string): Saved {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    if (raw && Array.isArray(raw.groups)) {
      const machine = raw.machine === true;
      const seen = new Set<number>();
      const groups = machine ? keptArrangement(machineId) : (raw.groups as unknown[]).map((g) => validGroup(g, seen)).filter((g): g is Group => !!g);
      const at = typeof raw.at === 'number' && (machine || groups.some((g) => g.id === raw.at)) ? (raw.at as number) : null;
      const lead = typeof raw.lead === 'number' ? (raw.lead as number) : null;
      return { tabs: { groups, at, page: PAGES.includes(raw.page) ? raw.page : null }, machine, lead };
    }
    const old = JSON.parse(localStorage.getItem(OLD_KEY) ?? 'null');
    return { tabs: old ? migrate(old) : emptyTabs(), machine: false, lead: null };
  } catch {
    return { tabs: emptyTabs(), machine: false, lead: null };
  }
}

const ARRANGEMENT_KEY = 'cmx.machineLayout';

/** The machine's groups as this browser last heard them, and whose (machine `machineId`): the next visit shows its rows
 *  grouped from the first frame, before the machine says them again (a round trip after connecting). */
export function keepArrangement(machineId: string | undefined, groups: Group[]) {
  if (!machineId) return;
  try {
    localStorage.setItem(ARRANGEMENT_KEY, JSON.stringify({ machine: machineId, groups }));
  } catch {
    /* private window */
  }
}

/** The arrangement kept here for machine `machineId` (none for another's). */
function keptArrangement(machineId: string | undefined): Group[] {
  try {
    const raw = JSON.parse(localStorage.getItem(ARRANGEMENT_KEY) ?? 'null');
    if (!machineId || raw?.machine !== machineId || !Array.isArray(raw.groups)) return [];
    const seen = new Set<number>();
    return (raw.groups as unknown[]).map((g) => validGroup(g, seen)).filter((g): g is Group => !!g);
  } catch {
    return [];
  }
}

/** Keep the workspace in this browser: once the machine keeps the groups (`machine`), only the row shown and the
 *  page. */
export function saveTabs(s: TabsState, machine: boolean) {
  const kept = machine ? { groups: [], at: s.at, lead: current(s)?.lead ?? null, page: s.page, machine: true } : s;
  try {
    localStorage.setItem(KEY, JSON.stringify(kept));
  } catch {
    /* private window */
  }
}

/** The status a row shows: the most urgent of its sessions' (needs you, then working, then done), else its own. */
export function rowStatus(lead: Session, sessions: Session[], s: TabsState): Session['status'] {
  const ids = rowSessions(s, lead.id);
  if (ids.length < 2) return lead.status;
  const st = new Set(sessions.filter((x) => ids.includes(x.id)).map((x) => x.status));
  return (['waiting', 'working', 'done'] as const).find((x) => st.has(x)) ?? lead.status;
}
